import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PreloadedQuery, useFragment, usePreloadedQuery } from "react-relay";

import { BookingInfo } from "@/features/BookingParts";
import {
  Box,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { BookingConfirmedModal } from "../BookingConfirmedModal/BookingConfirmedModal";
import { useInitializeBookingSummaryAnalyticsDataAtom } from "../BookingSummary/atoms/analytics/useInitializeBookingSummaryAnalyticsDataAtom";
import { isConfirmedBookingModalOpenAtom } from "../BookingSummary/atoms/stores";
import {
  BOOKING_SUMMARY_CONTENT_MAX_WIDTH,
  useIsBookingSummaryNarrow,
} from "../BookingSummary/bookingSummaryWidth";
import { BookingHeader } from "../BookingSummary/components/BookingHeader/BookingHeader";
import { BookingSummaryLayout } from "../BookingSummary/components/BookingSummaryLayout/BookingSummaryLayout";
import { DiningBookingSummaryQuery } from "./__generated__/DiningBookingSummaryQuery.graphql";
import { DiningBookingSummary_booking$key } from "./__generated__/DiningBookingSummary_booking.graphql";
import { DiningPolicySection } from "./components/DiningPolicySection/DiningPolicySection";
import { DiningReservationFooter } from "./components/DiningReservationFooter/DiningReservationFooter";
import { DiningReservationSection } from "./components/DiningReservationSection/DiningReservationSection";
import { DiningSpecialRequestsSection } from "./components/DiningSpecialRequestsSection/DiningSpecialRequestsSection";

export const DiningBookingSummary = ({
  diningSummaryRef,
  handleGoBack,
}: {
  diningSummaryRef: PreloadedQuery<DiningBookingSummaryQuery>;
  handleGoBack: () => void;
}) => {
  const data = usePreloadedQuery(
    DiningBookingSummaryQueryString,
    diningSummaryRef
  );
  const isNarrow = useIsBookingSummaryNarrow();
  const insets = useSafeAreaInsets();

  const booking = useFragment<DiningBookingSummary_booking$key>(
    DiningBookingSummaryFragmentString,
    data.node ?? null
  );
  const clubRef = data.session?.currentClientProfile?.member?.club ?? null;
  const [isConfirmedBookingModalOpen, setIsConfirmedBookingModalOpen] = useAtom(
    isConfirmedBookingModalOpenAtom
  );

  useInitializeBookingSummaryAnalyticsDataAtom(booking ?? null);

  return (
    <HStack
      width="100%"
      flex={1}
      style={{ paddingTop: insets.top, paddingBottom: insets.bottom }}
      backgroundColor="surface-secondary"
    >
      <Box backgroundColor={"surface-secondary"} flexShrink={1} flexGrow={1}>
        <Box
          maxWidth={BOOKING_SUMMARY_CONTENT_MAX_WIDTH}
          marginLeft="auto"
          height="100%"
          width="100%"
          marginTop={isNarrow ? 0 : 3}
          backgroundColor={"surface-primary"}
          marginRight={"auto"}
        >
          <BookingSummaryLayout
            header={
              <BookingHeader
                clubRef={clubRef}
                bookingRef={booking ?? null}
                handleGoBack={handleGoBack}
                title={t`Reservation Details`}
              />
            }
            body={
              <VStack
                gap={isNarrow ? 2 : 0}
                backgroundColor="surface-secondary"
              >
                <BookingInfo bookingRef={booking ?? null} />
                <DiningReservationSection bookingRef={booking ?? null} />
                <DiningSpecialRequestsSection bookingRef={booking ?? null} />
                <DiningPolicySection bookingRef={booking ?? null} />
              </VStack>
            }
            footer={
              <DiningReservationFooter
                bookingRef={booking ?? null}
                handleGoBack={handleGoBack}
              />
            }
          />
        </Box>
      </Box>
      {isConfirmedBookingModalOpen && booking ? (
        <BookingConfirmedModal
          isOpen={isConfirmedBookingModalOpen}
          setConfirmedBookingModal={setIsConfirmedBookingModalOpen}
          bookingRef={booking}
        />
      ) : null}
    </HStack>
  );
};
export const DiningBookingSummaryQueryString = graphql`
  query DiningBookingSummaryQuery($bookingId: ID!) {
    session {
      currentClientProfile {
        member {
          ... on Member {
            id
          }
          ... on Customer {
            id
          }

          club {
            slug
            ...BookingHeaderFragment_BookingSummary
          }
        }
      }
    }
    node(id: $bookingId) {
      ... on Booking {
        ...DiningBookingSummary_booking
      }
    }
  }
`;

const DiningBookingSummaryFragmentString = graphql`
  fragment DiningBookingSummary_booking on Booking {
    id
    ...useGetBookingAnalyticsMetadata_booking
    ...BookingHeaderFragment_booking
    ...BookingInfo_Booking
    ...DiningReservationSection_booking
    ...DiningSpecialRequestsSection_booking
    ...DiningPolicySection_booking
    ...DiningReservationFooter_booking
    ...BookingConfirmedModal_Booking
  }
`;
